import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { MaskLines, Reveal } from "./Reveal";
import { GrainButton } from "./GrainButton";
import { openInquiry } from "../lib/scroll";
import { REGIONS } from "../data/content";

const CA_PATH =
  "M 10 23 L 110 23 L 110 194 L 182 308 L 253 422 L 240 462 L 237 536 L 242 559 L 182 562 L 178 551 L 156 519 L 149 513 L 137 487 L 99 466 L 89 425 L 77 391 L 65 351 L 53 311 L 50 285 L 38 270 L 31 236 L 19 171 L 11 137 L 7 114 L 12 86 L 10 51 Z";

const POINTS = [
  { label: "San Francisco Bay Area", x: 52, y: 264 },
  { label: "Sacramento", x: 75, y: 218 },
  { label: "Stockton", x: 79, y: 252 },
  { label: "Modesto — Headquarters", x: 87, y: 271 },
  { label: "Sierra Foothills", x: 101, y: 228 },
  { label: "Merced", x: 99, y: 291 },
  { label: "Fresno Region", x: 115, y: 323 },
];

export const ServiceArea = () => {
  const [hovered, setHovered] = useState(null);

  return (
    <section id="service-area" data-testid="service-area-section" className="px-6 sm:px-10 lg:px-16 py-16 sm:py-36 max-w-[1700px] mx-auto">
      <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <div>
          <p className="font-mono text-[10px] sm:text-xs tracking-[0.28em] text-ember mb-5">WHERE WE WORK</p>
          <MaskLines
            as="h2"
            lines={[<>Local roots. <span className="font-serif italic font-normal">Regional reach.</span></>]}
            className="font-display font-medium tracking-[-0.03em] leading-[1.05] text-3xl sm:text-5xl lg:text-[4rem] text-charcoal"
          />
          <Reveal delay={0.15}>
            <p className="mt-6 text-sm sm:text-base text-charcoal/60 leading-relaxed max-w-xl">
              Based in Modesto, California, Nagel serves clients throughout Northern and Central California — from the Central Valley and San Francisco Bay Area to the Sierra Foothills.
            </p>
          </Reveal>
          <div className="mt-10 space-y-8">
            {REGIONS.map((r, i) => (
              <Reveal key={r.id} delay={0.2 + i * 0.1}>
                <div data-testid={`region-${r.id}`} className="group">
                  <p className="font-display font-medium tracking-[-0.02em] text-xl sm:text-2xl text-charcoal transition-colors duration-300 group-hover:text-ember">
                    {r.label}
                  </p>
                  <p className="mt-2 text-sm text-charcoal/55 leading-relaxed">{r.detail}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.4}>
            <p data-testid="service-area-stat" className="mt-12 font-display font-medium tracking-[-0.03em] text-6xl sm:text-8xl text-pine leading-none">
              24 <span className="text-2xl sm:text-4xl align-middle tracking-[-0.02em] text-charcoal/60 font-light">COUNTIES</span>
            </p>
          </Reveal>
          <Reveal delay={0.45}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <GrainButton testid="service-area-send-inquiry-button" onClick={() => openInquiry()}>
                SEND INQUIRY
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </GrainButton>
              <a
                data-testid="service-area-cta"
                href="tel:+12095451696"
                className="group inline-flex items-center gap-3 font-mono text-xs tracking-[0.2em] text-charcoal border border-charcoal/20 rounded-full px-7 py-3.5 hover:bg-charcoal hover:text-cream transition-colors duration-300"
              >
                TALK TO OUR TEAM
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <div className="relative">
            <svg
              data-testid="california-map"
              viewBox="0 0 260 570"
              className="w-full max-w-[400px] mx-auto"
              role="img"
              aria-label="Stylized map of California showing Nagel's service regions"
            >
              <defs>
                <pattern id="terrain-dots" width="13" height="13" patternUnits="userSpaceOnUse">
                  <circle cx="2" cy="2" r="1.1" fill="#1C3322" opacity="0.16" />
                </pattern>
                <linearGradient id="ca-fill" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#1C3322" stopOpacity="0.08" />
                  <stop offset="100%" stopColor="#2E6930" stopOpacity="0.14" />
                </linearGradient>
              </defs>

              <path d={CA_PATH} fill="url(#ca-fill)" stroke="#1C3322" strokeOpacity="0.35" strokeWidth="1.5" strokeLinejoin="round" />
              <path d={CA_PATH} fill="url(#terrain-dots)" stroke="none" />

              <path d="M 105 60 Q 130 180 160 300 T 235 430" fill="none" stroke="#1C3322" strokeOpacity="0.18" strokeWidth="1" strokeDasharray="1 5" strokeLinecap="round" />
              <path d="M 25 140 Q 55 260 48 360 T 75 500" fill="none" stroke="#1C3322" strokeOpacity="0.14" strokeWidth="1" strokeDasharray="1 5" strokeLinecap="round" />

              <ellipse cx="85" cy="310" rx="30" ry="120" fill="#F05A24" opacity="0.07">
                <animate attributeName="opacity" values="0.05;0.1;0.05" dur="5s" repeatCount="indefinite" />
              </ellipse>

              {POINTS.map((p) => (
                <g
                  key={p.label}
                  data-testid={`map-point-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onMouseEnter={() => setHovered(p.label)}
                  onMouseLeave={() => setHovered(null)}
                  className="cursor-pointer"
                >
                  <circle cx={p.x} cy={p.y} r="16" fill="transparent" />
                  {hovered === p.label && (
                    <circle cx={p.x} cy={p.y} r="11" fill="#F05A24" opacity="0.18" />
                  )}
                  <circle cx={p.x} cy={p.y} r={hovered === p.label ? 5.5 : 4} fill={hovered === p.label ? "#F05A24" : "#E65100"} style={{ transition: "r 0.3s" }}>
                    <animate attributeName="opacity" values="1;0.55;1" dur="2.6s" repeatCount="indefinite" />
                  </circle>
                  <circle cx={p.x} cy={p.y} r="4" fill="none" stroke="#F05A24" strokeWidth="1">
                    <animate attributeName="r" values="4;18" dur="2.6s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.5;0" dur="2.6s" repeatCount="indefinite" />
                  </circle>
                  {hovered === p.label && (
                    <text
                      x={Math.min(p.x + 12, 150)}
                      y={p.y - 10}
                      fontSize="10"
                      fontFamily="JetBrains Mono, monospace"
                      letterSpacing="1"
                      fill="#121314"
                    >
                      {p.label.toUpperCase()}
                    </text>
                  )}
                </g>
              ))}
            </svg>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap">
              <p
                data-testid="map-region-label"
                className="font-mono text-[10px] sm:text-xs tracking-[0.2em] text-charcoal/60 bg-cream/80 backdrop-blur px-4 py-2 rounded-full border border-charcoal/10 transition-opacity duration-300"
              >
                {hovered ? hovered.toUpperCase() : "NORTHERN & CENTRAL CALIFORNIA"}
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
